import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  BookOpen,
  Brain,
  Building2,
  ChevronRight,
  ExternalLink,
  Globe,
  MoreHorizontal,
  Plus,
  Search,
  Loader2,
  Sparkles,
  Trash2,
  Users as UsersIcon,
  X,
  Zap,
} from "lucide-react";
import UserContext from "../../../context/users";
import { AdminRole } from "../../../constants";
import {
  deleteNativeClientPlatform,
  getNativeClientPlatforms,
  getNativeClientPlatformStats,
} from "../../../services/native-client-platform";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Badge } from "../../../components/ui/badge";
import { ConfirmDialog } from "../../../components/ui/dialog";
import { DropdownMenu } from "../../../components/ui/dropdown-menu";
import { EmptyState } from "../../../components/ui/empty-state";
import { Pagination } from "../../../components/ui/pagination";
import { Spinner } from "../../../components/ui/spinner";
import { cn } from "../../../lib/cn";
import useDebounce from "../../../hooks/useDebounce";

export default function ListClientPlatforms() {
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [user] = useContext(UserContext);
  const isAdmin = user?.role === AdminRole;
  const scopedCompanyID = isAdmin ? undefined : user?.companyID;
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, active: 0, withPlan: 0 });
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    setStatsLoading(true);
    getNativeClientPlatformStats()
      .then(({ data }) => setStats(data))
      .catch(console.error)
      .finally(() => setStatsLoading(false));
  }, []);

  const reloadList = useCallback(() => {
    setLoading(true);
    const filters = { page, perPage };
    if (scopedCompanyID) filters.companyID = scopedCompanyID;
    if (debouncedSearch) {
      filters.search = debouncedSearch;
      filters.searchField = "name";
    }
    getNativeClientPlatforms(filters)
      .then(({ data: list, total: t }) => { setData(list || []); setTotal(t || 0); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [page, perPage, debouncedSearch, scopedCompanyID]);

  useEffect(() => { reloadList(); }, [reloadList]);
  useEffect(() => { setPage(1); }, [debouncedSearch]);

  return (
    <div className="dash-scroll flex h-full w-full flex-col overflow-y-auto bg-[var(--dash-bg)]">
      <div className="mx-auto w-full max-w-[1400px] px-6 py-8">
        <header className="mb-6 flex flex-col gap-5">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="flex flex-col gap-1">
              <h1 className="text-2xl font-semibold tracking-tight text-[var(--dash-text)]">Clientes</h1>
              {statsLoading ? (
                <div className="mt-1 flex items-center gap-2">
                  <div className="animate-pulse rounded-md bg-[var(--dash-surface-hover)] h-3.5 w-24" />
                  <span className="text-xs text-[var(--dash-text-faint)]">·</span>
                  <div className="animate-pulse rounded-md bg-[var(--dash-surface-hover)] h-3.5 w-16" />
                </div>
              ) : (
                <p className="text-xs text-[var(--dash-text-muted)]">
                  {stats.total} cliente{stats.total !== 1 ? "s" : ""} · {stats.active} ativo{stats.active !== 1 ? "s" : ""}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="w-60">
                <Input
                  leftIcon={Search}
                  placeholder="Buscar..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  rightSlot={<SearchFeedback loading={loading} hasValue={!!search} onClear={() => setSearch("")} />}
                />
              </div>
              <a href="https://verboo.ai/pt/docs/concepts/client-platforms" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" leftIcon={BookOpen}>Docs</Button>
              </a>
              <Button variant="secondary" leftIcon={Brain} onClick={() => navigate('/onboarding?flow=agent')}>Criar agente</Button>
              <Button variant="primary" leftIcon={Plus} onClick={() => navigate('/onboarding?flow=client')}>Configurar novo cliente</Button>
            </div>
          </div>

          {!statsLoading && (
            <div className="flex flex-wrap gap-3">
              <StatPill icon={Building2} label="Total" value={stats.total} />
              <StatPill icon={Zap} label="Ativos" value={stats.active} variant="success" />
              <StatPill icon={Brain} label="Com plano" value={stats.withPlan} />
            </div>
          )}
        </header>

        {loading && data.length === 0 ? (
          <ClientListSkeleton />
        ) : data.length === 0 ? (
          <div className="rounded-2xl border border-[var(--dash-border)] bg-[var(--dash-surface)]">
            <EmptyState
              icon={Sparkles}
              title={debouncedSearch ? "Nenhum resultado" : "Crie sua primeira plataforma"}
              description={debouncedSearch ? `Nada encontrado para "${debouncedSearch}".` : "Organize clientes, agentes e canais de atendimento em um só lugar."}
              action={!debouncedSearch && <Button variant="primary" size="lg" leftIcon={Plus} onClick={() => navigate('/onboarding?flow=client')}>Configurar primeiro cliente</Button>}
            />
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-[var(--dash-border)] bg-[var(--dash-surface)]">
            <div className="hidden items-center gap-4 border-b border-[var(--dash-border)] px-5 py-3 text-[10px] font-medium uppercase tracking-[0.12em] text-[var(--dash-text-muted)] md:flex">
              <div className="w-6" />
              <div className="flex-1">Nome</div>
              <div className="hidden w-40 lg:block">URL</div>
              <div className="hidden w-28 lg:block">Plano</div>
              <div className="w-20 text-right">Agentes</div>
              <div className="w-20 text-right">Usuários</div>
              <div className="w-8" />
            </div>
            <div className="divide-y divide-[var(--dash-border)]">
              {data.map((item, i) => (
                <ClientRow key={item.id} data={item} index={i} reloadList={reloadList} />
              ))}
            </div>
          </div>
        )}

        {total > perPage && <Pagination page={page} pageSize={perPage} total={total} onPageChange={setPage} />}
      </div>
    </div>
  );
}

function StatPill({ icon: Icon, label, value, variant }) {
  return (
    <div className="flex items-center gap-2 rounded-xl bg-[var(--dash-surface)] px-3 py-2 ring-1 ring-[var(--dash-border)]">
      <div className={cn("flex h-6 w-6 items-center justify-center rounded-md", variant === "success" ? "bg-[rgba(52,254,173,0.1)] text-[#1fb37c]" : "bg-[var(--dash-primary-soft)] text-[var(--dash-primary)]")}>
        <Icon size={12} strokeWidth={2.4} />
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-sm font-semibold tabular-nums text-[var(--dash-text)]">{value}</span>
        <span className="text-[10px] text-[var(--dash-text-muted)]">{label}</span>
      </div>
    </div>
  );
}

function ClientRow({ data, reloadList }) {
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const onDelete = useCallback(() => {
    setDeleting(true);
    deleteNativeClientPlatform(data.id)
      .then(() => { toast.success("Deletado!"); setConfirmDelete(false); reloadList(); })
      .catch(() => toast.error("Falha ao deletar"))
      .finally(() => setDeleting(false));
  }, [data.id, reloadList]);

  const url = data.customURL || (data.subdomain ? `${data.subdomain}.verboo.com.br` : null);
  const assistantsCount = data.assistants?.length || 0;
  const usersCount = data.users?.length || 0;
  const planName = data.nativeClientPlatformPlan?.name;

  const menuItems = useMemo(() => [
    { label: "Editar dados do cliente", icon: Building2, onClick: () => navigate(`${data.id}/general`) },
    { label: "Visitar dashboard", icon: ExternalLink, onClick: () => window.open(`https://verboo.ai/dashboard/${data.id}`, "_blank") },
    { separator: true },
    { label: "Deletar", icon: Trash2, danger: true, onClick: () => setConfirmDelete(true) },
  ], [data.id, navigate]);

  return (
    <>
      <div
        className="group flex cursor-pointer items-center gap-4 px-5 py-3.5 transition-colors hover:bg-[var(--dash-surface-hover)]"
        onClick={() => navigate(`${data.id}/agents`)}
      >
        <div className="flex w-6 justify-center">
          <div className={cn("h-2.5 w-2.5 rounded-full ring-2", data.isActive !== false ? "bg-[#34fead] ring-[rgba(52,254,173,0.25)]" : "bg-[var(--dash-text-faint)] ring-[var(--dash-border)]")} />
        </div>

        <div className="flex min-w-0 flex-1 items-center gap-3">
          {data.logo ? (
            <img src={data.logo} alt="" className="h-7 w-7 shrink-0 rounded-lg object-cover ring-1 ring-[var(--dash-border)]" />
          ) : (
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-[9px] font-bold uppercase text-white" style={{ background: data.primaryColor || "var(--dash-primary)" }}>
              {data.name?.substring(0, 2)}
            </div>
          )}
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="truncate text-sm font-medium text-[var(--dash-text)]">{data.name}</span>
            <div className="flex items-center gap-2 text-[11px] text-[var(--dash-text-muted)] md:hidden">
              {url && <span className="truncate">{url}</span>}
              <span>{assistantsCount} agentes</span>
            </div>
          </div>
        </div>

        <div className="hidden w-40 lg:block">
          {url ? (
            <span className="flex items-center gap-1 truncate text-[11px] text-[var(--dash-text-muted)]">
              <Globe size={10} className="shrink-0" />{url}
            </span>
          ) : (
            <span className="text-[11px] text-[var(--dash-text-faint)]">—</span>
          )}
        </div>

        <div className="hidden w-28 lg:block">
          {planName ? <Badge variant="primary">{planName}</Badge> : <span className="text-[11px] text-[var(--dash-text-faint)]">Sem plano</span>}
        </div>

        <div className="hidden w-20 text-right md:block">
          <span className="text-sm font-semibold tabular-nums text-[var(--dash-text)]">{assistantsCount}</span>
        </div>

        <div className="hidden w-20 text-right md:block">
          <span className="text-sm tabular-nums text-[var(--dash-text-soft)]">{usersCount}</span>
        </div>

        <div className="w-8" onClick={(e) => e.stopPropagation()}>
          <DropdownMenu
            align="right"
            trigger={<button type="button" className="flex h-7 w-7 items-center justify-center rounded-lg text-[var(--dash-text-muted)] opacity-0 transition-all hover:bg-[var(--dash-surface-subtle)] group-hover:opacity-100"><MoreHorizontal size={14} /></button>}
            items={menuItems}
          />
        </div>

        <ChevronRight size={13} className="shrink-0 text-[var(--dash-text-faint)] opacity-0 transition-opacity group-hover:opacity-100" />
      </div>

      <ConfirmDialog open={confirmDelete} onClose={() => setConfirmDelete(false)} onConfirm={onDelete} title="Deletar cliente?" description={`"${data.name}" será removido permanentemente.`} confirmLabel="Deletar" loading={deleting} />
    </>
  );
}

function SearchFeedback({ loading, hasValue, onClear }) {
  if (loading && hasValue) {
    return <Loader2 size={14} className="animate-spin text-[var(--dash-primary)]" />;
  }
  if (hasValue) {
    return (
      <button
        type="button"
        onClick={onClear}
        className="flex h-5 w-5 items-center justify-center rounded-md text-[var(--dash-text-muted)] transition-colors hover:bg-[var(--dash-surface-hover)] hover:text-[var(--dash-text)]"
      >
        <X size={12} />
      </button>
    );
  }
  return null;
}

function Bone({ className, delay = 0, style }) {
  return (
    <div
      className={cn("animate-pulse rounded-lg", className)}
      style={{ background: "var(--dash-surface-hover)", animationDelay: `${delay}ms`, ...style }}
    />
  );
}

const SKEL_WIDTHS = [160, 120, 180, 140, 150, 110, 170, 130];

function ClientListSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap gap-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-center gap-2 rounded-xl bg-[var(--dash-surface)] px-3 py-2 ring-1 ring-[var(--dash-border)]">
            <Bone className="h-6 w-6 !rounded-md" delay={i * 80} />
            <Bone className="h-4 w-8" delay={i * 80 + 30} />
            <Bone className="h-3 w-10" delay={i * 80 + 50} />
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-2xl border border-[var(--dash-border)] bg-[var(--dash-surface)]">
        <div className="hidden items-center gap-4 border-b border-[var(--dash-border)] px-5 py-3 text-[10px] font-medium uppercase tracking-[0.12em] text-[var(--dash-text-faint)] md:flex">
          <div className="w-6" />
          <div className="flex-1">Nome</div>
          <div className="hidden w-40 lg:block">URL</div>
          <div className="hidden w-28 lg:block">Plano</div>
          <div className="w-20 text-right">Agentes</div>
          <div className="w-20 text-right">Usuários</div>
          <div className="w-8" />
        </div>

        <div className="divide-y divide-[var(--dash-border)]">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 px-5 py-3.5">
              <div className="flex w-6 justify-center">
                <Bone className="h-2.5 w-2.5 !rounded-full" delay={i * 60} />
              </div>
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <Bone className="h-7 w-7 !rounded-lg" delay={i * 60 + 20} />
                <Bone className="h-4" delay={i * 60 + 40} style={{ width: SKEL_WIDTHS[i % SKEL_WIDTHS.length] }} />
              </div>
              <div className="hidden w-40 lg:block">
                <Bone className="h-3 w-28" delay={i * 60 + 50} />
              </div>
              <div className="hidden w-28 lg:block">
                <Bone className="h-5 w-16 !rounded-full" delay={i * 60 + 60} />
              </div>
              <div className="hidden w-20 md:flex justify-end">
                <Bone className="h-4 w-6" delay={i * 60 + 70} />
              </div>
              <div className="hidden w-20 md:flex justify-end">
                <Bone className="h-4 w-6" delay={i * 60 + 80} />
              </div>
              <div className="w-8" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
